import { useEffect, useState, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { NeuroSuiteInfo } from '../../../components';
import styled from '@emotion/styled';
import { useDots } from '../../../helpers/forth-valley-hooks';
import { VisualFieldMatrix } from '../../../2022-component-library';
import PatientResponseControls from '../../../components/VisualFieldForthValley/PatientResponseControls';
import StimulusControls from '../../../components/VisualFieldForthValley/StimulusControls';
import { useResultData } from '../../../helpers/useResultData';
import { Button, SessionActions, useSessionUI, breakpoints } from '../../../ui';
import { StepBody, ConfidenceMeter, Note, useConfirmLeave } from '../testScreenKit';

const Layout = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 24px 32px;
  align-items: start;
  @media (max-width: 900px) and (orientation: portrait), (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ center }) => (center ? 'center' : 'stretch')};
  gap: 16px;
  width: 100%;
  min-width: 0;
`;

// Stroke Suite (visualFieldForthValley) - Landolt C field test. No Figma frame; built in the
// Visual Field (NEW) language: matrix left, key + values + controls right.

const NeuroSuiteTest = ({ updateStep }) => {
  const { testData, updateTestData } = useTestContext();
  const [totalDots, setTotalDots] = useState(0);
  const [boundariesWarning, setBoundariesWarning] = useState(false);
  const [canStartTest, setCanStartTest] = useState(true);
  const [canProgress, setCanProgress] = useState(false);
  const postMessage = usePostMessage();
  const messageFromPatient = useReceiveMessage();
  const { confirm } = useSessionUI();
  const confirmLeave = useConfirmLeave();
  const { clearAllResults } = useResultData();

  const [testProgressData, setTestProgressData] = useState({});

  const getDotData = useDots();

  useEffect(() => {
    if (messageFromPatient.type === 'testProgressUpdate') {
      setTestProgressData(messageFromPatient.content);
      if (messageFromPatient.content.testEnded) setCanProgress(true);
    }
  }, [messageFromPatient]);

  const startTest = () => {
    postMessage({ type: 'beginTest', content: testData });
    setCanStartTest(false);
  };

  const stopTest = () => {
    postMessage({ type: 'stopTest', content: testData });
    clearResults('noConfirmation');
  };

  const clearResults = async (noConfirmNeeded) => {
    if (noConfirmNeeded || (await confirm({ message: 'Are you sure you want to clear these results and begin the test again?', tone: 'danger' }))) {
      postMessage({ type: 'resetTest' });
      initialiseDotData();
      setCanStartTest(true);
    }
  };

  const initialiseDotData = useCallback(() => {
    const { localDots, localTotalDots, hasBoundariesWarning } = getDotData();
    updateTestData({ forthValleyVisualFieldDots: localDots });
    setTotalDots(localTotalDots);
    setBoundariesWarning(hasBoundariesWarning);
  }, [getDotData, updateTestData]);

  useEffect(() => {
    initialiseDotData();
  }, [initialiseDotData]);

  useEffect(() => {
    if (!testProgressData['activeDot']) {
      initialiseDotData();
    }
  }, [testProgressData, initialiseDotData]);

  useEffect(() => {
    //handle results
    return () => updateTestData({ visualFieldForthValley: { ...testData.visualFieldForthValley, results: testProgressData } });
  }, [testProgressData, updateTestData]);

  const confidence = testProgressData?.testConfidence === 0 ? '0' : testProgressData?.testConfidence || 100;

  return (
    <StepBody gap={20} data-testid='neuro-test'>
      <Layout>
        <Column center>
          <VisualFieldMatrix testInProgress={!canStartTest} testProgressData={testProgressData} pointIsBeingDisplayed={testProgressData?.currentLandoltCQuadrant} />
          <StimulusControls testProgressData={testProgressData} canStartTest={canStartTest} />
        </Column>
        <Column>
          <NeuroSuiteInfo boundariesWarning={boundariesWarning} totalDots={totalDots} testProgressData={testProgressData} />
          <PatientResponseControls testProgressData={testProgressData} />
          <ConfidenceMeter percentage={confidence} />
          {testProgressData?.testConfidence === 0 && <Note tone='error'>Test ended due to low confidence.</Note>}
        </Column>
      </Layout>
      <SessionActions
        onBack={() =>
          confirmLeave().then(() => {
            clearAllResults();
            updateTestData({ testInProgress: false });
            updateStep('familiarisationPhase');
          })
        }
        primary={{ label: 'End Test', onClick: () => updateStep('results') }}
      >
        <Button variant='success' size='xl' disabled={!canStartTest} onClick={() => startTest()}>
          Start
        </Button>
        <Button variant={canStartTest ? 'secondary' : 'destructive'} size='xl' disabled={canStartTest} onClick={() => (testProgressData.testEnded ? clearResults() : stopTest())}>
          {testProgressData.testEnded ? 'Reset' : 'Stop'}
        </Button>
      </SessionActions>
    </StepBody>
  );
};

export default NeuroSuiteTest;
